import React, { useMemo } from 'react';
import styled from 'styled-components';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { CommonButton } from '../../../../Styles/CommonStyles';

const ModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1400px',
  maxHeight: '80vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '8px',
  overflow: 'auto'
};

const ModalTitle = styled.h2`
  margin-bottom: 20px;
  color: #1976d2;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 10px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

const QuantityField = styled(TextField)`
  margin-bottom: 10px;
  margin-right: 10px;
`;

const ActionButton = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
  background-color: #1976d2;
`;

const ErrorMessageBox = styled.div`
  background-color: #f44336;
  color: white;
  padding: 12px 16px;
  border-radius: 4px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  animation: slideDown 0.3s ease-out;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.3);
  
  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ErrorIcon = styled.span`
  margin-right: 10px;
  font-size: 18px;
`;

const ModifyConsumablesModal = ({
  open,
  onClose,
  selectedConsumables,
  onQuantityChange,
  onPartNumberChange,
  onSplitRow,
  onRemoveSplitRow,
  onConfirm,
  getAvailableQuantity,
  getPartNumberOptions,
  getGroupTotalQuantity,
  availableQtyLabel = "Office Available Qty", // Allow customization of the label
  showBoIssuedColumn = false, // Only show "Issued at BO" column for Store L1, not for BO itself
  error // Pass error from parent component
}) => {
  console.log(selectedConsumables);
  // Memoize the modal TableRow rendering function
  const renderModalTableRows = useMemo(() => {
    // Group items by groupId to add visual separators
    let lastGroupId = null;
    
    return selectedConsumables.map((item, index) => {
      const isNewGroup = lastGroupId !== null && lastGroupId !== item.groupId;
      lastGroupId = item.groupId;
      
      // Calculate max allowed for this specific row
      const itemBoIssued = item.boIssuedQuantity || 0;
      const itemOwnCapacity = item.modReqQuantity - itemBoIssued;
      const currentItemQty = parseInt(item.selectedQuantity, 10) || 0;
      
      // Find all rows with same Consumable, Asset, and AssetTag (the "pool")
      const poolRows = selectedConsumables.filter(row => 
        row.consumable === item.consumable &&
        row.asset === item.asset &&
        (row.assettag?.label === item.assettag?.label)
      );
      
      // Calculate total spare capacity in the pool
      const poolSpareCapacity = poolRows.reduce((sum, row) => {
        const spare = Math.max(0, 
          row.modReqQuantity - (row.boIssuedQuantity || 0) - (parseInt(row.selectedQuantity, 10) || 0)
        );
        return sum + spare;
      }, 0);
      
      let maxAllowedForThisRow;
      
      // BASE ROW: Use own capacity, but can also borrow from pool if needed
      if (!item.isSplit) {
        // Can use own capacity + pool spare if it has exhausted own capacity
        maxAllowedForThisRow = itemOwnCapacity + poolSpareCapacity;
      }
      // SPLIT ROW: Can borrow from pool
      else {
        // This split item can increase up to: current + pool spare capacity
        maxAllowedForThisRow = currentItemQty + poolSpareCapacity;
      }
      
      // Also check store availability for non-BO items
      let finalMaxAllowed = maxAllowedForThisRow;
      if (!item.isBoIssued && item.partno) {
        const storeAvailable = getAvailableQuantity(item.partno);
        finalMaxAllowed = Math.min(maxAllowedForThisRow, storeAvailable);
      }
    
      // Check if this row can be split
      // Row can only be split if: Requested Qty > Issued at BO + Issue Qty
      const rowSpareCapacity = Math.max(0,
        item.modReqQuantity - (item.boIssuedQuantity || 0) - (parseInt(item.selectedQuantity, 10) || 0)
      );
      const canSplit = rowSpareCapacity > 0;
    
    return (
      <TableRow 
        key={`modal-consumable-${index}`}
        sx={{ 
          backgroundColor: item.isBoIssued ? '#e8f4f8' : (item.isSplit ? '#f5f5f5' : 'inherit'),
          borderTop: isNewGroup ? '2px solid #1976d2' : 'inherit'
        }}
      >
        <TableCell>{index + 1}</TableCell>
        <TableCell>
          {item.consumable}
          {item.isBoIssued && <span style={{ fontSize: '0.75rem', color: '#1976d2', marginLeft: '5px', fontWeight: 'bold' }}>🔒 BO Issued</span>}
          {item.isSplit && !item.isBoIssued && <span style={{ fontSize: '0.75rem', color: '#1976d2', marginLeft: '5px', fontWeight: 'bold' }}>↳ Split</span>}
        </TableCell>
        <TableCell>{item.asset}</TableCell>
        <TableCell>{item.assettag?.label || 'Not Applicable'}</TableCell>
        <TableCell>
          {item.isBoIssued ? (
            // Read-only display for BO issued items
            <TextField
              size="small"
              value={item.partno || ''}
              disabled
              sx={{ minWidth: 150 }}
            />
          ) : (
            // Editable autocomplete for Store L1 items
            <Autocomplete
              size="small"
              options={getPartNumberOptions(item.consumable, item.asset, item.assettag, index)}
              value={item.partno || null}
              onChange={(event, newValue) => onPartNumberChange(index, newValue)}
              renderInput={(params) => <TextField {...params} label="Select Part No." />}
              sx={{ minWidth: 150 }}
            />
          )}
        </TableCell>
        <TableCell>
          {item.partno ? getAvailableQuantity(item.partno) : '-'}
        </TableCell>
        <TableCell>{item.modReqQuantity}</TableCell>
        {showBoIssuedColumn && <TableCell>{item.boIssuedQuantity || 0}</TableCell>}
        <TableCell>
            <QuantityField
              type="number"
              size="small"
              value={item.selectedQuantity}
              onChange={(e) => onQuantityChange(index, e.target.value)}
              disabled={!item.partno}
              inputProps={{
                min: 0,
                max: finalMaxAllowed
              }}
              helperText={item.partno ? `Max: ${finalMaxAllowed}` : 'Select part number first'}
            />
          </TableCell>
        <TableCell>
          <div style={{ display: 'flex', gap: '5px' }}>
            <Tooltip title={canSplit ? "Split into multiple rows" : "Cannot split: No spare capacity (Requested Qty must be > Issued at BO + Issue Qty)"}>
              <span>
                <IconButton 
                  size="small" 
                  color="primary" 
                  onClick={() => onSplitRow(index)}
                  disabled={!canSplit}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
              {item.isSplit && (
                <Tooltip title="Remove split row">
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={() => onRemoveSplitRow(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </TableCell>
        </TableRow>
      );
    });
  }, [selectedConsumables, onQuantityChange, getAvailableQuantity, getPartNumberOptions, onPartNumberChange, onSplitRow, onRemoveSplitRow, showBoIssuedColumn]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modify-consumables-modal"
    >
      <Box sx={ModalStyle}>
        <ModalTitle>Modify Consumable Quantities</ModalTitle>
        
        {/* Display error message if validation fails */}
        {error && (
          <ErrorMessageBox>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ErrorIcon>⚠️</ErrorIcon>
              <span>{error}</span>
            </div>
          </ErrorMessageBox>
        )}
        
        <p>Adjust the quantities to be issued for each consumable item:</p>
        
        <TableContainer component={Paper}>
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableCell>Sl.No.</TableCell>
                <TableCell>Consumable</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell>Asset Tag</TableCell>
                <TableCell>Part No.</TableCell>
                <TableCell>{availableQtyLabel}</TableCell>
                <TableCell>Requested Qty</TableCell>
                {showBoIssuedColumn && <TableCell>Issued at BO</TableCell>}
                <TableCell>Issue Qty</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {renderModalTableRows}
            </TableBody>
          </Table>
        </TableContainer>
        
        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e3f2fd', borderRadius: '4px' }}>
          <strong>How to Split Consumables:</strong>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            <li>Click the <AddIcon fontSize="small" style={{ verticalAlign: 'middle' }} /> button to split a consumable into multiple rows</li>
            <li><strong>Each row can have a different part number</strong> - Example: User requested 5 toners → Assign 3 of Part A + 2 of Part B</li>
            <li>When you first select a part number, the quantity auto-fills with remaining amount</li>
            <li><strong>Adjust quantities independently</strong> - Each row has its own Issue Qty field with a "Max" indicator</li>
            <li>The <strong>Group Total</strong> column shows the sum - total cannot exceed requested quantity</li>
            <li>Changing part numbers preserves your quantity selections</li>
          </ul>
        </div>

        <ModalFooter>
          <Button onClick={onClose}>Cancel</Button>
          <ActionButton onClick={onConfirm}>Confirm Changes</ActionButton>
        </ModalFooter>
      </Box>
    </Modal>
  );
};

export default ModifyConsumablesModal;

